/**
 * MotivosAdmin Page
 * Admin interface for managing calendar day motivos (reasons)
 */
import { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { motivos as motivosApi } from '../services/api';
import { Plus, Edit2, Trash2, Check, X, Tag, Loader2 } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';

const MotivosAdmin = () => {
    const toast = useToast();
    const [motivos, setMotivos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingMotivo, setEditingMotivo] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        color: '#ef4444',
        descripcion: '',
        tipo_hora_sindical: ''
    });
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, motivo: null });

    // Predefined colors for quick selection
    const presetColors = [
        '#ef4444', // red
        '#f97316', // orange
        '#eab308', // yellow
        '#22c55e', // green
        '#14b8a6', // teal
        '#3b82f6', // blue
        '#8b5cf6', // violet
        '#ec4899', // pink
        '#6b7280', // gray
    ];

    useEffect(() => {
        loadMotivos();
    }, []);

    const loadMotivos = async () => {
        try {
            setLoading(true);
            const data = await motivosApi.getAll();
            // Ensure data is always an array
            setMotivos(Array.isArray(data) ? data : (data?.motivos || []));
        } catch (error) {
            toast.error('Error al cargar motivos');
            console.error(error);
            setMotivos([]);
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setEditingMotivo(null);
        setFormData({ nombre: '', color: '#ef4444', descripcion: '', tipo_hora_sindical: '' });
        setShowModal(true);
    };

    const openEditModal = (motivo) => {
        setEditingMotivo(motivo);
        setFormData({
            nombre: motivo.nombre,
            color: motivo.color,
            descripcion: motivo.descripcion || '',
            tipo_hora_sindical: motivo.tipo_hora_sindical || ''
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingMotivo(null);
        setFormData({ nombre: '', color: '#ef4444', descripcion: '', tipo_hora_sindical: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        try {
            setSaving(true);
            if (editingMotivo) {
                await motivosApi.update(editingMotivo.id, formData);
                toast.success('Motivo actualizado');
            } else {
                await motivosApi.create(formData);
                toast.success('Motivo creado');
            }
            closeModal();
            loadMotivos();
        } catch (error) {
            toast.error(error.message || 'Error al guardar motivo');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (motivo) => {
        try {
            await motivosApi.update(motivo.id, { activo: !motivo.activo });
            toast.success(motivo.activo ? 'Motivo desactivado' : 'Motivo activado');
            loadMotivos();
        } catch (error) {
            toast.error('Error al cambiar estado');
        }
    };

    const handleDelete = async (motivo) => {
        setDeleteConfirm({ isOpen: true, motivo });
    };

    const confirmDelete = async () => {
        const motivo = deleteConfirm.motivo;
        if (!motivo) return;

        try {
            await motivosApi.delete(motivo.id);
            toast.success('Motivo eliminado');
            loadMotivos();
        } catch (error) {
            toast.error(error.message || 'Error al eliminar motivo');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Tag className="w-7 h-7 text-red-400" />
                        Motivos de Calendario
                    </h1>
                    <p className="text-gray-400 mt-1">
                        Gestiona los motivos que los conductores pueden usar para marcar días especiales
                    </p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Nuevo Motivo
                </button>
            </div>

            {/* Motivos List */}
            <div className="card">
                {motivos.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                        <Tag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No hay motivos configurados</p>
                        <p className="text-sm">Crea el primero para empezar</p>
                    </div>
                ) : (
                    <div className="divide-y divide-white/10">
                        {motivos.map((motivo) => (
                            <div
                                key={motivo.id}
                                className={`flex items-center justify-between p-4 ${!motivo.activo ? 'opacity-50' : ''}`}
                            >
                                <div className="flex items-center gap-4">
                                    {/* Color preview */}
                                    <div
                                        className="w-10 h-10 rounded-xl shadow-lg"
                                        style={{ backgroundColor: motivo.color }}
                                    />
                                    <div>
                                        <h3 className="font-semibold text-white flex items-center gap-2">
                                            {motivo.nombre}
                                            {!motivo.activo && (
                                                <span className="text-xs bg-gray-600 px-2 py-0.5 rounded-full">
                                                    Inactivo
                                                </span>
                                            )}
                                        </h3>
                                        {motivo.descripcion && (
                                            <p className="text-sm text-gray-400">{motivo.descripcion}</p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">
                                            Color: {motivo.color}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    {/* Toggle active */}
                                    <button
                                        onClick={() => handleToggleActive(motivo)}
                                        className={`p-2 rounded-lg transition-colors ${motivo.activo
                                            ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                            : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                                            }`}
                                        title={motivo.activo ? 'Desactivar' : 'Activar'}
                                    >
                                        {motivo.activo ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                    </button>

                                    {/* Edit */}
                                    <button
                                        onClick={() => openEditModal(motivo)}
                                        className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-colors"
                                        title="Editar"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>

                                    {/* Delete (soft) */}
                                    <button
                                        onClick={() => handleDelete(motivo)}
                                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                        title="Eliminar"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm bg-black/70">
                    <div className="bg-gradient-to-br from-[#1a1a3e] to-[#0f0f2a] rounded-2xl shadow-2xl border border-cyan-500/20 w-full max-w-md">
                        <div className="p-6 border-b border-white/10">
                            <h2 className="text-xl font-bold text-white">
                                {editingMotivo ? 'Editar Motivo' : 'Nuevo Motivo'}
                            </h2>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {/* Nombre */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Nombre *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Ej: Baja médica"
                                    className="input-field"
                                    autoFocus
                                />
                            </div>

                            {/* Color */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Color
                                </label>
                                <div className="flex items-center gap-3 mb-3">
                                    <div
                                        className="w-12 h-12 rounded-xl shadow-lg border-2 border-white/20"
                                        style={{ backgroundColor: formData.color }}
                                    />
                                    <input
                                        type="text"
                                        value={formData.color}
                                        onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                        placeholder="#ef4444"
                                        className="input-field flex-1"
                                        pattern="^#[0-9A-Fa-f]{6}$"
                                    />
                                </div>
                                {/* Preset colors */}
                                <div className="flex gap-2 flex-wrap">
                                    {presetColors.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setFormData({ ...formData, color })}
                                            className={`w-8 h-8 rounded-lg transition-transform hover:scale-110 ${formData.color === color ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0f0f2a]' : ''
                                                }`}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Descripción */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Descripción (opcional)
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    placeholder="Breve descripción del motivo..."
                                    rows={3}
                                    className="input-field resize-none"
                                />
                            </div>

                            {/* Tipo Especial (Vinculación Lógica) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Tipo de Horas Especiales
                                </label>
                                <select
                                    value={formData.tipo_hora_sindical}
                                    onChange={(e) => setFormData({ ...formData, tipo_hora_sindical: e.target.value })}
                                    className="input-field cursor-pointer"
                                >
                                    <option value="">Motivo Estándar (Sin lógica especial)</option>
                                    <option value="reunion_sindical">Reunión Sindical (Suma horas)</option>
                                    <option value="asamblea">Asamblea de Trabajadores (Suma horas)</option>
                                    <option value="consumo_horas_sindicales">Día Libre Sindical (Consume horas)</option>
                                </select>
                                <p className="mt-2 text-[10px] text-gray-500 italic">
                                    * Selecciona una opción solo si quieres que este motivo active la lógica de visualización bipartida y el conteo de horas acumuladas.
                                </p>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="btn-secondary flex-1"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4" />
                                            {editingMotivo ? 'Guardar' : 'Crear'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, motivo: null })}
                onConfirm={confirmDelete}
                title="Eliminar Motivo"
                message={`¿Estás seguro de que deseas eliminar el motivo "${deleteConfirm.motivo?.nombre}"?`}
                confirmText="Eliminar"
                variant="danger"
            />
        </div>
    );
};

export default MotivosAdmin;
